import React, { useImperativeHandle, useState } from "react";
import {
  Box,
  styled,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { WithLoadingSpinner } from "@components/common";
import { useCashflowAnalyzerContext } from "@providers";
import { ChartLayout, Figure } from "../BaseChartTools";
import { BarChartToolbar, BarChartYear } from "./components";
import { BarChartItem } from "./types";

const BarChartContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
}));

const Circle = styled("div")<{ color: string }>(({ color }) => ({
  width: "8px",
  height: "8px",
  backgroundColor: color,
  borderRadius: "50%",
  flexShrink: 0,
}));

export type BarChartProps = {
  data: BarChartItem[];
  previousYearData: BarChartItem[];
  nextYearData: BarChartItem[];
  isFetching: boolean;
};

export const BarChart = ({
  data,
  previousYearData,
  nextYearData,
  isFetching,
}: BarChartProps) => {
  const { t } = useTranslation();
  const isLessSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const theme = useTheme();
  const [selectedBar, setSelectedBar] = useState<string | null>(null);
  const [isAnimationEnabled, setIsAnimationEnabled] = useState(true);
  const [transitionDirection, setTransitionDirection] = useState<
    "left" | "right" | null
  >(null);

  const {
    mode,
    mainPeriod,
    intervalType,
    setSubPeriod,
    setMainPeriod,
    barChartRef,
  } = useCashflowAnalyzerContext();

  const startYear = mainPeriod.startDate.getFullYear();
  const endYear = mainPeriod.endDate.getFullYear();
  const displayedYear =
    startYear === endYear ? startYear : [startYear, endYear].join(" - ");

  useImperativeHandle(
    barChartRef,
    () => ({
      resetActiveItem: () => setSelectedBar(null),
    }),
    []
  );

  const handleMainPeriodChange = (period: Required<RangeType>) => {
    setIsAnimationEnabled(false);
    setTransitionDirection(
      period.startDate < mainPeriod.startDate ? "left" : "right"
    );
    setTimeout(() => {
      setTransitionDirection(null);
      setMainPeriod(period);
      if (!selectedBar) {
        setSubPeriod(period);
      }
    }, 300);
  };

  const onBarClick = (item: BarChartItem, changeSub = true) => {
    if (selectedBar === item.id) {
      setSelectedBar(null);
      setSubPeriod({
        startDate: mainPeriod.startDate,
        endDate: mainPeriod.endDate,
      });
    } else {
      setSubPeriod({
        startDate: item.startDate,
        endDate: item.endDate,
      });
      setSelectedBar(item.id);
    }
  };

  const commonProps = {
    mode,
    mainPeriod,
    onBarClick,
    selectedBar,
    isAnimationEnabled,
    transitionDirection,
    isFetching,
    intervalType,
  };

  return (
    <ChartLayout
      loaderHeight="342px"
      intervalSelector={
        <BarChartToolbar
          onMainPeriodChange={handleMainPeriodChange}
          isFetching={isFetching}
        />
      }
      figure={
        <Figure>
          <Box pt={theme.spacing(2.5)}>
            <Typography
              variant="caption"
              fontWeight={600}
              color="textSecondary"
            >
              {t("cashflowAnalyzer.chart.period")}
            </Typography>
            <Typography variant="subtitle2" mb={1}>
              {displayedYear}
            </Typography>
          </Box>
        </Figure>
      }
    >
      <WithLoadingSpinner isLoading={isFetching}>
        <BarChartContainer>
          <BarChartYear data={previousYearData} {...commonProps} />
          <BarChartYear data={data} {...commonProps} currentYearBlock />
          <BarChartYear data={nextYearData} {...commonProps} />
        </BarChartContainer>
      </WithLoadingSpinner>
      <Box
        display="flex"
        justifyContent={isLessSm ? "center" : "flex-end"}
        gap={6}
        mt={theme.spacing(1)}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Circle color={theme.palette.primary.dark} />
          <Typography variant="body3" color="textSecondary">
            {t("cashflowAnalyzer.expenses")}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Circle color={theme.palette.success.main} />
          <Typography variant="body3" color="textSecondary">
            {t("cashflowAnalyzer.income")}
          </Typography>
        </Box>
      </Box>
    </ChartLayout>
  );
};
